import React, { useState, useRef, useEffect } from 'react';
import { validatePhone, COUNTRY_CONFIGS } from '../utils/formatters';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8',
    building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11',
    mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z M12 10a3 3 0 10-6 0 3 3 0 006 0z',
    dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
    settings: 'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z',
    image: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2z M8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21',
    chevronDown: 'M6 9l6 6 6-6',
    x: 'M18 6L6 18 M6 6l12 12',
};

const TABS = ['General Info', 'Address & Contact', 'Financials & Tax', 'Operations'];

const defaultForm = {
    // General Info
    companyName: '', category: '', industry: '', registrationNumber: '', website: '', companyLogo: null,
    // Address & Contact
    streetAddress: '', city: '', stateProvince: '', postalCode: '', country: '',
    primaryPhone: '', primaryEmail: '', password: '', contactPersonName: '', contactPersonPhone: '',
    // Financials & Tax
    taxId: '', defaultCurrency: 'UAE Dirham (AED)', vatPercentage: '', fiscalYearStart: '',
    bankName: '', accountHolderName: '', accountName: '', accountNumber: '', iban: '', swiftCode: '',
    // Operations
    defaultShiftStart: '', defaultShiftEnd: '', fleetSize: '', numberOfDrivers: '',
    primaryServiceType: [],   // now an array
    timezone: '',
    emergencyContactName: '', emergencyPhone: '',
    // Phone Codes (UI only, combined on save)
    primaryPhoneCode: '+971', contactPersonPhoneCode: '+971', emergencyPhoneCode: '+971'
};

const CURRENCIES = [
    'UAE Dirham (AED)', 'US Dollar (USD)', 'Euro (EUR)', 'British Pound (GBP)',
    'Saudi Riyal (SAR)', 'Kuwaiti Dinar (KWD)', 'Qatari Riyal (QAR)', 'Indian Rupee (INR)',
];

const SERVICE_TYPES = [
    'Employee Transportation', 'School Transportation', 'Airport Transfer',
    'Tourism & Excursions', 'Medical Transport', 'Charter & Rental', 'Other',
];

const MultiSelect = ({ label, options, selected, onToggle, onRemove }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="op-field" style={{ marginTop: 12 }} ref={dropdownRef}>
            <label className="op-label">{label} <span style={{ color: '#6366f1', fontSize: 11.14, fontWeight: 500, marginLeft: 4 }}>Select all that apply</span></label>
            <div className="op-multiselect-container">
                <div
                    className={`op-multiselect-input ${isOpen ? 'active' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {selected.length === 0 ? (
                        <span className="op-multiselect-placeholder">Choose service types...</span>
                    ) : (
                        selected.map(val => (
                            <div key={val} className="op-chip" onClick={(e) => e.stopPropagation()}>
                                {val}
                                <span className="op-chip-remove" onClick={() => onRemove(val)}>
                                    <Icon d={icons.x} size={10} strokeWidth={3} />
                                </span>
                            </div>
                        ))
                    )}
                    <span className="op-multiselect-chevron">
                        <Icon d={icons.chevronDown} size={16} strokeWidth={2.5} />
                    </span>
                </div>

                {isOpen && (
                    <div className="op-multiselect-dropdown">
                        {options.map(opt => {
                            const isSel = selected.includes(opt);
                            return (
                                <div
                                    key={opt}
                                    className={`op-multiselect-option ${isSel ? 'selected' : ''}`}
                                    onClick={() => onToggle(opt)}
                                >
                                    <div className="op-checkbox">
                                        <div className="op-checkbox-inner">
                                            <Icon d={icons.check} size={10} strokeWidth={4} color="white" />
                                        </div>
                                    </div>
                                    {opt}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// editOperator: if provided, the form will open in edit mode pre-filled
// onSaved: callback after a successful create or update
export default function CreateOperator({ editOperator = null, onSaved, user }) {
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(defaultForm);
    const [countries, setCountries] = useState([]);
    const [logoPreview, setLogoPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [categories, setCategories] = useState([]);
    const fileRef = useRef();

    useEffect(() => {
        fetch('/api/countries')
            .then(r => r.json())
            .then(data => setCountries(data.filter(c => c.isActive !== false)))
            .catch(err => console.error('Error fetching countries:', err));

        fetch('/api/categories')
            .then(r => r.json())
            .then(data => setCategories(data.filter(c => c.isActive !== false)))
            .catch(err => console.error('Error fetching categories:', err));
    }, []);

    // Helper to pull code out of full phone string
    const splitPhone = (full, countryList) => {
        if (!full) return { code: '+971', num: '' };
        // Sort countries by code length descending to match longest code first (e.g. +123 before +1)
        const sorted = [...countryList].sort((a, b) => b.code.length - a.code.length);
        const match = sorted.find(c => full.startsWith(c.code));
        if (match) {
            return { code: match.code, num: full.replace(match.code, '') };
        }
        return { code: '+971', num: full };
    };

    // When editOperator changes, pre-fill the form
    useEffect(() => {
        if (editOperator) {
            const filled = { ...defaultForm };
            Object.keys(defaultForm).forEach(key => {
                if (editOperator[key] !== undefined && editOperator[key] !== null) {
                    if (key === 'category' && typeof editOperator[key] === 'object') {
                        filled[key] = editOperator[key]._id;
                    } else {
                        filled[key] = editOperator[key];
                    }
                }
            });

            // Splitting phones
            const p1 = splitPhone(editOperator.primaryPhone, countries);
            filled.primaryPhoneCode = p1.code;
            filled.primaryPhone = p1.num;

            const p2 = splitPhone(editOperator.contactPersonPhone, countries);
            filled.contactPersonPhoneCode = p2.code;
            filled.contactPersonPhone = p2.num;

            const p3 = splitPhone(editOperator.emergencyPhone, countries);
            filled.emergencyPhoneCode = p3.code;
            filled.emergencyPhone = p3.num;

            // Ensure primaryServiceType is always an array
            if (!Array.isArray(filled.primaryServiceType)) {
                filled.primaryServiceType = filled.primaryServiceType
                    ? [filled.primaryServiceType]
                    : [];
            }
            filled.companyLogo = null;
            setForm(filled);
            if (editOperator.companyLogo) {
                setLogoPreview(`/api/uploads/${editOperator.companyLogo}`);
            } else {
                setLogoPreview(null);
            }
            setActiveTab(0);
        } else {
            setForm(defaultForm);
            setLogoPreview(null);
            setActiveTab(0);
        }
    }, [editOperator, countries]);

    const isEditMode = !!editOperator;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleLogo = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm(f => ({ ...f, companyLogo: file }));
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleCountryChange = (e) => {
        const countryName = e.target.value;
        const matched = countries.find(c => c.name === countryName);
        setForm(f => ({
            ...f,
            country: countryName,
            primaryPhoneCode: matched ? matched.code : f.primaryPhoneCode,
            contactPersonPhoneCode: matched ? matched.code : f.contactPersonPhoneCode,
            emergencyPhoneCode: matched ? matched.code : f.emergencyPhoneCode
        }));
    };

    const handleSave = async () => {
        if (!form.companyName.trim()) {
            showToast('Company Name is required.', 'error');
            return;
        }

        // Validate phone numbers based on country
        if (form.primaryPhone && !validatePhone(form.primaryPhone, form.country)) {
            showToast(`Primary Phone is invalid for ${form.country || 'the selected country'}.`, 'error');
            return;
        }
        if (form.contactPersonPhone && !validatePhone(form.contactPersonPhone, form.country)) {
            showToast(`Contact Person Phone is invalid for ${form.country || 'the selected country'}.`, 'error');
            return;
        }
        if (form.emergencyPhone && !validatePhone(form.emergencyPhone, form.country)) {
            showToast(`Emergency Phone is invalid for ${form.country || 'the selected country'}.`, 'error');
            return;
        }

        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => {
                if (k === 'companyLogo' && v instanceof File) {
                    fd.append('companyLogo', v);
                } else if (k === 'primaryServiceType') {
                    // Send each selected service type as a repeated key
                    const arr = Array.isArray(v) ? v : (v ? [v] : []);
                    arr.forEach(s => fd.append('primaryServiceType', s));
                } else if (k === 'primaryPhone') {
                    fd.append(k, form.primaryPhoneCode + v);
                } else if (k === 'contactPersonPhone') {
                    fd.append(k, form.contactPersonPhoneCode + v);
                } else if (k === 'emergencyPhone') {
                    fd.append(k, form.emergencyPhoneCode + v);
                } else if (!k.endsWith('PhoneCode') && k !== 'companyLogo') {
                    fd.append(k, v);
                }
            });

            const url = isEditMode
                ? `/api/operators/${editOperator._id}`
                : '/api/operators';
            const method = isEditMode ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                body: fd,
                headers: {
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                }
            });
            const data = await res.json();
            if (res.ok) {
                showToast(isEditMode ? 'Operator updated successfully!' : 'Operator saved successfully!', 'success');
                if (!isEditMode) {
                    setForm(defaultForm);
                    setLogoPreview(null);
                    setActiveTab(0);
                }
                if (onSaved) onSaved(data.operator);
            } else {
                showToast(data.message || 'Failed to save operator', 'error');
            }
        } catch (err) {
            showToast('Server error. Is the backend running?', 'error');
        } finally {
            setSaving(false);
        }
    };

    const toggleServiceType = (type) => {
        setForm(f => {
            const current = Array.isArray(f.primaryServiceType) ? f.primaryServiceType : [];
            return {
                ...f,
                primaryServiceType: current.includes(type)
                    ? current.filter(t => t !== type)
                    : [...current, type],
            };
        });
    };

    return (
        <div className="create-operator-page">
            {toast && (
                <div className={`op-toast ${toast.type}`}>{toast.msg}</div>
            )}

            {/* Header */}
            <div className="op-header">
                <div>
                    <h2 className="op-title">
                        {isEditMode ? `Edit Operator — ${editOperator.companyName || ''}` : 'Operator Onboarding'}
                    </h2>
                    <p className="op-subtitle">
                        {isEditMode
                            ? 'Update the operator details below and save your changes.'
                            : "Manage your organisation's details, branding, and operational configurations."}
                    </p>
                </div>
                <button className="op-save-btn" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {saving ? 'Saving…' : (
                        <>
                            <Icon d={icons.save} size={16} />
                            {isEditMode ? 'Update Changes' : 'Save Changes'}
                        </>
                    )}
                </button>
            </div>

            {/* Tabs */}
            <div className="op-tabs">
                {TABS.map((tab, i) => (
                    <button
                        key={tab}
                        className={`op-tab${activeTab === i ? ' active' : ''}`}
                        onClick={() => setActiveTab(i)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="op-body">

                {/* ── General Info ── */}
                {activeTab === 0 && (
                    <div className="op-section">
                        <div className="op-section-header orange">
                            <div className="op-section-icon"><Icon d={icons.building} size={20} color="white" /></div>
                            <div>
                                <div className="op-section-title">General Information</div>
                                <div className="op-section-desc">Basic identity details of your organisation.</div>
                            </div>
                        </div>
                        <div className="op-grid-2">
                            <Field label="Company Name" required name="companyName" value={form.companyName} onChange={handleChange} placeholder="e.g. ViaRidez Transport By Bus Rental L.L.C" />
                            <div className="op-field">
                                <label className="op-label">Category</label>
                                <select name="category" value={form.category} onChange={handleChange} className="op-input op-select">
                                    <option value="">Select Category</option>
                                    {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
                                </select>
                            </div>
                            <Field label="Industry" name="industry" value={form.industry} onChange={handleChange} placeholder="e.g. Transportation" />
                            <Field label="Registration Number" name="registrationNumber" value={form.registrationNumber} onChange={handleChange} placeholder="e.g. CN-123456" />
                            <Field label="Website" name="website" value={form.website} onChange={handleChange} placeholder="e.g. www.example.ae" />
                        </div>
                        {/* Logo */}
                        <div className="op-logo-row">
                            <label className="op-label">Company Logo</label>
                            <div className="op-logo-area">
                                {logoPreview
                                    ? <img src={logoPreview} alt="logo" className="op-logo-preview" />
                                    : <div className="op-logo-placeholder"><Icon d={icons.image} size={32} color="#cbd5e1" /></div>
                                }
                                <div className="op-logo-right">
                                    <button className="op-upload-btn" onClick={() => fileRef.current.click()}>Upload Logo</button>
                                    <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.gif,.svg" style={{ display: 'none' }} onChange={handleLogo} />
                                    <span className="op-logo-hint">Max 5MB. PNG, JPG, SVG supported.</span>
                                    <span className="op-logo-hint">This logo will be displayed in the sidebar, reports, and invoices generated by the system.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Address & Contact ── */}
                {activeTab === 1 && (
                    <div className="op-section">
                        <div className="op-section-header red">
                            <div className="op-section-icon"><Icon d={icons.mapPin} size={20} color="white" /></div>
                            <div>
                                <div className="op-section-title">Address &amp; Contact</div>
                                <div className="op-section-desc">Location and communication details.</div>
                            </div>
                        </div>
                        {/* Street full-width */}
                        <div className="op-field" style={{ marginBottom: 12 }}>
                            <label className="op-label">Street Address</label>
                            <input
                                type="text"
                                name="streetAddress"
                                value={form.streetAddress}
                                onChange={handleChange}
                                placeholder="e.g. The Regal Tower, Office 2960, 29th Floor, Business Bay, Dubai - UAE"
                                className="op-input"
                            />
                        </div>
                        <div className="op-grid-2">
                            <Field label="City" name="city" value={form.city} onChange={handleChange} placeholder="e.g. Dubai" />
                            <Field label="State / Province" name="stateProvince" value={form.stateProvince} onChange={handleChange} placeholder="e.g. Dubai" />
                            <Field label="Postal Code" name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="e.g. 12345" />
                            <div className="op-field">
                                <label className="op-label">Country</label>
                                <select name="country" value={form.country} onChange={handleCountryChange} className="op-input op-select">
                                    <option value="">Select Country</option>
                                    {countries.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="op-grid-2" style={{ marginTop: 12 }}>
                            <PhoneField
                                label="Primary Phone"
                                name="primaryPhone"
                                value={form.primaryPhone}
                                code={form.primaryPhoneCode}
                                countries={countries}
                                onChange={handleChange}
                                onCodeChange={(c) => setForm(f => ({ ...f, primaryPhoneCode: c }))}
                            />
                            <Field label="Primary Email" name="primaryEmail" value={form.primaryEmail} onChange={handleChange} placeholder="e.g. info@example.ae" type="email" />
                            <Field label="Password" name="password" value={form.password} onChange={handleChange} placeholder="Enter login password" type="password" />
                            <Field label="Contact Person Name" name="contactPersonName" value={form.contactPersonName} onChange={handleChange} placeholder="e.g. John Doe" />
                            <PhoneField
                                label="Contact Person Phone"
                                name="contactPersonPhone"
                                value={form.contactPersonPhone}
                                code={form.contactPersonPhoneCode}
                                countries={countries}
                                onChange={handleChange}
                                onCodeChange={(c) => setForm(f => ({ ...f, contactPersonPhoneCode: c }))}
                            />
                        </div>
                    </div>
                )}

                {/* ── Financials & Tax ── */}
                {activeTab === 2 && (
                    <div className="op-section">
                        <div className="op-section-header yellow">
                            <div className="op-section-icon"><Icon d={icons.dollar} size={20} color="white" /></div>
                            <div>
                                <div className="op-section-title">Financials &amp; Tax</div>
                                <div className="op-section-desc">Billing, currency, and banking information.</div>
                            </div>
                        </div>
                        <div className="op-grid-2">
                            <Field label="Tax ID / VAT Number" name="taxId" value={form.taxId} onChange={handleChange} placeholder="e.g. TRN-100123456789003" />
                            <div className="op-field">
                                <label className="op-label">Default Currency</label>
                                <select name="defaultCurrency" value={form.defaultCurrency} onChange={handleChange} className="op-input op-select">
                                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <Field label="VAT Percentage (%)" name="vatPercentage" value={form.vatPercentage} onChange={handleChange} placeholder="e.g. 5" type="number" />
                            <Field label="Fiscal Year Start" name="fiscalYearStart" value={form.fiscalYearStart} onChange={handleChange} type="date" />
                        </div>
                        <div className="op-subsection-label">Banking Details</div>
                        <div className="op-grid-2">
                            <Field label="Bank Name" name="bankName" value={form.bankName} onChange={handleChange} placeholder="e.g. Emirates NBD" />
                            <Field label="Account Holder Name" name="accountHolderName" value={form.accountHolderName} onChange={handleChange} placeholder="e.g. ViaRidez Transport L.L.C" />
                            <Field label="Account Name" name="accountName" value={form.accountName} onChange={handleChange} placeholder="e.g. Corporate Account" />
                            <Field label="Account Number" name="accountNumber" value={form.accountNumber} onChange={handleChange} placeholder="e.g. 1234567890" />
                            <Field label="IBAN" name="iban" value={form.iban} onChange={handleChange} placeholder="e.g. AE070331234567890123456" />
                            <Field label="SWIFT Code" name="swiftCode" value={form.swiftCode} onChange={handleChange} placeholder="e.g. EBILAEAD" />
                        </div>
                    </div>
                )}

                {/* ── Operations ── */}
                {activeTab === 3 && (
                    <div className="op-section">
                        <div className="op-section-header teal">
                            <div className="op-section-icon"><Icon d={icons.settings} size={20} color="white" /></div>
                            <div>
                                <div className="op-section-title">Operations</div>
                                <div className="op-section-desc">Operating hours and service configuration.</div>
                            </div>
                        </div>
                        <div className="op-grid-2">
                            <Field label="Default Shift Start" name="defaultShiftStart" value={form.defaultShiftStart} onChange={handleChange} type="time" />
                            <Field label="Default Shift End" name="defaultShiftEnd" value={form.defaultShiftEnd} onChange={handleChange} type="time" />
                            <Field label="Fleet Size" name="fleetSize" value={form.fleetSize} onChange={handleChange} placeholder="e.g. 25" type="number" />
                            <Field label="Number of Drivers" name="numberOfDrivers" value={form.numberOfDrivers} onChange={handleChange} placeholder="e.g. 30" type="number" />
                        </div>
                        {/* ── NEW Multi-Select Service Types ── */}
                        <MultiSelect
                            label="Service Type"
                            options={SERVICE_TYPES}
                            selected={form.primaryServiceType}
                            onToggle={toggleServiceType}
                            onRemove={(type) => toggleServiceType(type)}
                        />
                        <div className="op-grid-2" style={{ marginTop: 12 }}>
                            <Field label="Timezone" name="timezone" value={form.timezone} onChange={handleChange} placeholder="e.g. Asia/Dubai" />
                        </div>
                        <div className="op-subsection-label">Emergency Contacts</div>
                        <div className="op-grid-2">
                            <Field label="Emergency Contact Name" name="emergencyContactName" value={form.emergencyContactName} onChange={handleChange} placeholder="e.g. Jane Smith" />
                            <PhoneField
                                label="Emergency Phone"
                                name="emergencyPhone"
                                value={form.emergencyPhone}
                                code={form.emergencyPhoneCode}
                                countries={countries}
                                onChange={handleChange}
                                onCodeChange={(c) => setForm(f => ({ ...f, emergencyPhoneCode: c }))}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, name, value, onChange, placeholder, type = 'text', required }) {
    return (
        <div className="op-field">
            <label className="op-label">
                {label}{required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="op-input"
            />
        </div>
    );
}

function PhoneField({ label, name, value, code, countries, onChange, onCodeChange }) {
    return (
        <div className="op-field">
            <label className="op-label">{label}</label>
            <div style={{ display: 'flex', gap: 8 }}>
                <select
                    value={code}
                    onChange={(e) => onCodeChange(e.target.value)}
                    className="op-input op-select"
                    style={{ width: '85px', flexShrink: 0 }}
                >
                    {countries && countries.length > 0 ? (
                        countries.map(c => (
                            <option key={c._id || c.code} value={c.code}>{c.code}</option>
                        ))
                    ) : (
                        Object.values(COUNTRY_CONFIGS).map(c => (
                            <option key={c.phoneCode} value={c.phoneCode}>{c.phoneCode}</option>
                        ))
                    )}
                </select>
                <input
                    type="text"
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder="Phone number"
                    className="op-input"
                    style={{ flex: 1 }}
                />
            </div>
        </div>
    );
}
